/**
 * spotlight(el, { label, ms }): pulse an outline + optional label on an element
 * so the human can see what the agent just touched. CSS only; honours
 * prefers-reduced-motion (static outline, no pulse).
 */

export interface SpotlightOptions {
  label?: string;
  ms?: number;
  /** Scroll the element into view first (default true). */
  scroll?: boolean;
}

let styleInjected = false;
function injectStyle() {
  if (styleInjected) return;
  styleInjected = true;
  const s = document.createElement('style');
  s.textContent = `
@keyframes wm-spot-pulse{0%{box-shadow:0 0 0 0 var(--wm-spot,rgba(59,108,255,.55))}70%{box-shadow:0 0 0 10px rgba(59,108,255,0)}100%{box-shadow:0 0 0 0 rgba(59,108,255,0)}}
.wm-spotlight{position:relative;outline:2px solid var(--accent,#3b6cff);outline-offset:3px;border-radius:inherit;animation:wm-spot-pulse 1.2s ease-out infinite}
.wm-spotlight__label{position:fixed;font:600 .7rem/1.4 system-ui,sans-serif;letter-spacing:.04em;background:var(--accent,#3b6cff);color:#fff;padding:.1em .5em;border-radius:4px;pointer-events:none;white-space:nowrap;z-index:950;transform:translateY(-100%);margin-top:-4px}
@media (prefers-reduced-motion:reduce){.wm-spotlight{animation:none;box-shadow:0 0 0 4px rgba(59,108,255,.25)}}
`;
  document.head.appendChild(s);
}

const active = new WeakMap<Element, { timer: number; label?: HTMLElement; unlisten?: () => void }>();

export function spotlight(el: Element | null | undefined, opts: SpotlightOptions = {}): () => void {
  if (!el) return () => {};
  injectStyle();
  const { label, ms = 2200, scroll = true } = opts;

  // Restart if already spotlit.
  const prev = active.get(el);
  if (prev) {
    clearTimeout(prev.timer);
    prev.label?.remove();
    prev.unlisten?.();
  }

  if (scroll) el.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  el.classList.add('wm-spotlight');

  // The label lives on <body> (position: fixed) so we never mutate the target's
  // children; <output>.value / textContent of the target stays untouched.
  let labelEl: HTMLElement | undefined;
  let unlisten: (() => void) | undefined;
  if (label) {
    labelEl = document.createElement('span');
    labelEl.className = 'wm-spotlight__label';
    labelEl.textContent = label;
    labelEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(labelEl);
    const place = () => {
      const r = el.getBoundingClientRect();
      labelEl!.style.left = `${Math.max(4, r.left)}px`;
      labelEl!.style.top = `${r.top}px`;
    };
    place();
    window.addEventListener('scroll', place, { passive: true, capture: true });
    window.addEventListener('resize', place, { passive: true });
    unlisten = () => {
      window.removeEventListener('scroll', place, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', place);
    };
  }

  const clear = () => {
    el.classList.remove('wm-spotlight');
    labelEl?.remove();
    unlisten?.();
    active.delete(el);
  };
  const timer = window.setTimeout(clear, ms);
  active.set(el, { timer, label: labelEl, unlisten });
  return () => {
    clearTimeout(timer);
    clear();
  };
}
